import type { CSSProperties } from '@linaria/core';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { memo, useCallback, useMemo } from 'react';
import type { Settings } from '../../../../types/state';
import { SettingsType } from '../../../constants/enums';
import { getAnimationsEnabledfromUeopt } from '../../../mfcLogic/settings';
import { addSearchHistoryItem } from '../../../state/state.search';
import { trackAnalyticsEvent } from '../../../utilities/analytics';
import { getModelColor } from '../../../utilities/colors';
import { useSearch } from '../../Hooks/useSearch';
import { useUserExists, useUserSelector } from '../../Hooks/useUser';
import BadgeAvatar from '../../common/BadgeAvatar';
import { StyledContainer, StyledDetailsContainer, StyledMediaContainer, StyledModelName, StyledRoot, StyledTopic } from '../../common/SearchResultStyles';
import CounterViewers from '../CounterViewers';
import PreviewPosterImage from '../PreviewPosterImage';
import HighlightedMatch from '../Search/HighlightedMatch';
import TagsPanel from '../TagsPanel';
import "./SearchResultAlt.linaria.module.css!=!./SearchResultAlt?./SearchResultAlt.linaria.module.css?css=H4sIAAAAAAAAA61SS5KbMBDd5ySZhSgYY+xBh6EEaglh/SzJIEzl7iljwIOT7LKheN3vI7U68Zk0Nrox8cwpJfrJEkqF5sgJ3oYyxb9+JD63p/7IE389ku5ymlqYe+QWDFZCowWfMxtnetaOOY2Q+Izb1BdhUsRxoRez7trZSBOfEaE7ppYmCsaWuY1YkbgaaqPhaUgCUyN/1yyGtr5d+npCA9QXERCTEBEVDpogjC4bI29KY6T8Pzp/r1LhrSRjubrWJv5ReyhfxSXgO3EmPI6oCu4Op+m/mpKgqjatWOBCfu7ROtPnG57txj/JKgN1L2Q6NUYaV/bE/URo/v9YSc25yg4nYN3nG5yY0QF5cYdFt+FN21ZZW5judt+jiUjBNRIBlC8b0AHcquDXKhvz3Ej2BtdL1CYEo+bdeIWMhUo536NdyPyqPhAXvgUJahnP9+gt5rlSXxoO53w3Iw+N0ZS4EQWI4QM/vsj04Jg0QwlSCuuFx0MrAiBvSQOlNoMjFm+kVlAKGs9TG54rfkxTvC3z/iQHG/Fr4F/z9X8DC56vtK8DAAA="
const PREVIEW_HEIGHT = 81;
const PREVIEW_WIDTH = 144;
const _exp = () => StyledContainer;
const StyledAltContainer = /*#__PURE__*/styled(_exp())({
  name: "StyledAltContainer",
  class: "s1lopxry",
  propsAsIs: true
});
const _exp2 = () => StyledDetailsContainer;
const StyledAltDetailsContainer = /*#__PURE__*/styled(_exp2())({
  name: "StyledAltDetailsContainer",
  class: "s4p7v5g",
  propsAsIs: true
});
const _exp4 = () => StyledMediaContainer;
const StyledAltMediaContainer = /*#__PURE__*/styled(_exp4())({
  name: "StyledAltMediaContainer",
  class: "s1hy4dxe",
  propsAsIs: true
});
const _exp5 = () => StyledTopic;
const StyledAltTopic1Container = /*#__PURE__*/styled(_exp5())({
  name: "StyledAltTopic1Container",
  class: "sjqjpxd",
  propsAsIs: true
});
const _exp6 = () => StyledTopic;
const StyledAltTopic2Container = /*#__PURE__*/styled(_exp6())({
  name: "StyledAltTopic2Container",
  class: "s1atfmyg",
  propsAsIs: true
});
const StyledAltNameCounterContainer = /*#__PURE__*/styled('div')({
  name: "StyledAltNameCounterContainer",
  class: "spbukvb",
  propsAsIs: false
});
const StyledHeaderContainer = /*#__PURE__*/styled('div')({
  name: "StyledHeaderContainer",
  class: "sm6gr37",
  propsAsIs: false
});
const avatarStyleOverrides = "atm_h0_ftgil2";
const stylesStyledModelName = "atm_7l_1emz6l0";
const stylesFontSize = "atm_c8_137efj2";
const stylesStyledHeaderContainer = "atm_h_1h6ojuz atm_gq_1y44olf";
const stylesStyledHeaderContainerTopic = "atm_h_1y6m0gg atm_gq_idpfg4";
export const StyledTopicTag = /*#__PURE__*/styled('p')({
  name: "StyledTopicTag",
  class: "s9ne384",
  propsAsIs: false
});
type SearchResultProps = {
  id: number;
  darkMode: boolean;
  isMobile: boolean;
  onOpenMenu: (slug: string, options?: any) => void;
  onOpenRoom: (slug: string) => void;
  onTagClick?: (tag: string) => void;
  searchString: string;
  settings: Settings[SettingsType.DEFAULT];
  cellWidth?: number;
  source: string;
  /** every result list the model matched - a combined search can match more than one */
  sources?: string[];
};
const SearchResultAlt = memo<SearchResultProps>(props => {
  const {
    id,
    darkMode,
    onOpenMenu,
    onOpenRoom,
    onTagClick,
    searchString,
    cellWidth,
    settings,
    source,
    sources
  } = props;
  const resolvedSources = sources || (source ? [source] : []);
  const matchesTopic = resolvedSources.includes('topic');
  const matchesTags = resolvedSources.includes('tags');
  const {
    query
  } = useSearch();
  const {
    search: searchQuery
  } = query;
  const {
    animatePreviews
  } = settings;
  const staticImage = !getAnimationsEnabledfromUeopt(animatePreviews);
  const [loading, exists] = useUserExists(id);
  const isBroadcasting = useUserSelector(id, 'isBroadcasting');
  const isOffline = useUserSelector(id, 'isOffline');
  const isModel = useUserSelector(id, 'isModel');
  const viewers = useUserSelector(id, 'viewers');
  const color = useUserSelector(id, 'color');
  const topic = useUserSelector(id, 'topic');
  const name = useUserSelector(id, 'name');
  const slug = useUserSelector(id, 'slug');
  const tags = useUserSelector(id, 'tags');
  const modelColor = getModelColor(color, darkMode);
  const onClickOpenMenu = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    trackAnalyticsEvent({
      action: `SearchResult_ClickThrough`,
      data: {
        openMenu: true
      }
    });
    onOpenMenu(slug);
    addSearchHistoryItem(searchQuery);
  }, [onOpenMenu, searchQuery, slug]);
  const onOpenContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    trackAnalyticsEvent({
      action: `SearchResult_ClickThrough`,
      data: {
        openMenu: true,
        contextMenu: true
      }
    });
    onOpenMenu(slug);
  }, [name]);
  const onClickOpenRoom = useCallback(() => {
    trackAnalyticsEvent({
      action: `SearchResult_ClickThrough`,
      data: {
        openRoom: true
      }
    });
    onOpenRoom(slug);
    addSearchHistoryItem(searchQuery);
  }, [onOpenRoom, searchQuery, slug]);
  const tagsResults = useMemo(() => {
    if (!tags || !matchesTags || !searchString) {
      return null;
    }
    const lowerCaseSearchString = searchString.toLocaleLowerCase();
    const matched = tags.split('  ').filter(tag => {
      return tag.toLocaleLowerCase().indexOf(lowerCaseSearchString) !== -1;
    }).map(tag => {
      return {
        value: <HighlightedMatch searchString={searchString} source={tag} />,
        key: tag
      };
    });
    return matched.length ? matched : null;
  }, [matchesTags, searchString, tags]);
  const availableTopicWidth = useMemo(() => {
    return cellWidth - PREVIEW_WIDTH - 12; // padding
  }, [cellWidth]);
  const topicFontSize = useMemo(() => {
    if (availableTopicWidth > 350) {
      return 10;
    }
    if (availableTopicWidth > 300) {
      return 9;
    }
    return 8.4;
  }, [availableTopicWidth]);
  if (loading || exists === false) {
    return null;
  }
  return <StyledRoot data-testid="Search__Result__Container" onContextMenu={onOpenContextMenu}>
            <StyledAltContainer>
                <StyledAltMediaContainer>
                    <PreviewPosterImage onClick={onClickOpenRoom} staticImage={staticImage} height={PREVIEW_HEIGHT} width={PREVIEW_WIDTH} id={id} rounded />
                </StyledAltMediaContainer>
                <StyledAltDetailsContainer data-testid="Search__Result" onClick={onClickOpenRoom}>
                    <div>
                        <StyledHeaderContainer className={matchesTopic ? stylesStyledHeaderContainerTopic : stylesStyledHeaderContainer}>
                            <BadgeAvatar isBroadcasting={isBroadcasting} onClick={onClickOpenMenu} isOffline={isOffline} addBoxShadow alt={name} size={36} id={id} className={avatarStyleOverrides} />
                            <StyledAltNameCounterContainer>
                                <StyledModelName style={{
                '--color': modelColor
              } as CSSProperties} data-testid="Search__Result__Name" className={stylesStyledModelName}>
                                    <HighlightedMatch source={name} searchString={searchString} />
                                </StyledModelName>
                                {isModel && !isOffline && <CounterViewers viewers={viewers} size={0.5} />}
                            </StyledAltNameCounterContainer>
                        </StyledHeaderContainer>
                        {matchesTopic && <StyledAltTopic1Container style={{
            '--font-size': topicFontSize + 'px'
          } as CSSProperties} className={cx('mfc-age-gate', stylesFontSize)}>
                                <HighlightedMatch searchString={searchString} source={topic} />
                            </StyledAltTopic1Container>}
                    </div>
                    {!matchesTags && !matchesTopic && <StyledAltTopic2Container style={{
          '--font-size': topicFontSize + 'px'
        } as CSSProperties} className={cx('mfc-age-gate', stylesFontSize)}>
                            {topic}
                        </StyledAltTopic2Container>}
                    {!!tagsResults && <div>
                            {!matchesTopic && <StyledTopicTag className="mfc-age-gate">
                                    {topic}
                                </StyledTopicTag>}
                            <TagsPanel onTagClick={onTagClick} tags={tagsResults} compact />
                        </div>}
                </StyledAltDetailsContainer>
            </StyledAltContainer>
        </StyledRoot>;
});
export default SearchResultAlt;